import type { ReactNode } from 'react';
import { Building2, User, Phone, Mail, MapPin, ShieldCheck, Globe2, Clock, CheckCircle2, XCircle, ScrollText, CalendarRange } from 'lucide-react';

export interface PartnerProfileIssuer {
  address: string | null;
  contact: string | null;
  email: string | null;
}

export interface PartnerProfileData {
  name: string;
  designation: string | null;
  type: string;
  status: string;
  city: string | null;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  show_on_website: boolean;
  hours: string | null;
  map_link: string | null;
  description: string | null;
  drug_license_no: string | null;
  tin_no: string | null;
  nid_no: string | null;
  bin_no: string | null;
  agreement_terms: string | null;
  agreement_start_date: string | null;
  agreement_end_date: string | null;
  documents: Record<string, string>;
  created_at: string;
}

// A clean, corporate "company dossier" look — slate/blue palette instead
// of the invoice system's navy/gold, since this reads as a business
// profile record rather than a financial document.
const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const BORDER = '#E2E8F0';
const PANEL = '#F8FAFC';
const ACCENT = '#2563EB';
const ACCENT_DEEP = '#1E3A8A';
const SUCCESS = '#16A34A';

const DOC_LABELS: Record<string, string> = {
  drug_license: 'Drug License',
  tin: 'TIN Certificate',
  nid: 'NID',
  photo: 'Photo',
  bin: 'BIN Certificate',
  bank_solvency: 'Bank Solvency',
  bank_statement: 'Bank Statement',
};
const DOC_ORDER = ['drug_license', 'tin', 'nid', 'bin', 'photo', 'bank_solvency', 'bank_statement'];

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '—';
  return (words[0][0] + (words[1]?.[0] ?? '')).toUpperCase();
}

function IconRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: PANEL, color: ACCENT }}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="m-0 text-[8.5px] font-bold uppercase tracking-[0.1em]" style={{ color: FAINT }}>
          {label}
        </p>
        <p className="m-0 mt-0.5 text-[12px] font-medium" style={{ color: value ? INK : FAINT }}>
          {value || 'Not provided'}
        </p>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <p className="m-0 text-[9.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: ACCENT_DEEP }}>
        {children}
      </p>
      <div className="flex-1" style={{ height: '1px', background: BORDER }} />
    </div>
  );
}

export function PartnerProfileDocument({
  partner,
  logoUrl,
  issuer,
}: {
  partner: PartnerProfileData;
  logoUrl?: string | null;
  issuer?: PartnerProfileIssuer;
}) {
  const displayName = partner.name?.trim() || 'Untitled Partner';
  const typeLabel = partner.type.charAt(0).toUpperCase() + partner.type.slice(1);
  const statusColors: Record<string, { bg: string; fg: string }> = {
    active: { bg: '#DCFCE7', fg: '#15803D' },
    inactive: { bg: '#F1F5F9', fg: '#475569' },
    prospect: { bg: '#DBEAFE', fg: '#1D4ED8' },
    suspended: { bg: '#FEE2E2', fg: '#B91C1C' },
  };
  const statusColor = statusColors[partner.status] ?? statusColors.inactive;

  const complianceIds = [
    partner.drug_license_no ? { label: 'Drug License No.', value: partner.drug_license_no } : null,
    partner.tin_no ? { label: 'TIN No.', value: partner.tin_no } : null,
    partner.nid_no ? { label: 'NID No.', value: partner.nid_no } : null,
    partner.bin_no ? { label: 'BIN No.', value: partner.bin_no } : null,
  ].filter((x): x is { label: string; value: string } => !!x);

  return (
    <div
      className="partner-profile-doc bg-white mx-auto"
      style={{
        width: '210mm',
        height: '278mm',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Inter', system-ui, sans-serif",
        boxSizing: 'border-box',
        color: INK,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      {/* Top bar — logo on one side, our own company details on the other */}
      <div
        className="flex items-start justify-between flex-shrink-0"
        style={{ padding: '6mm 16mm 3mm', borderBottom: `1px solid ${BORDER}` }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="The.K Seoul" crossOrigin="anonymous" style={{ height: '72px', maxWidth: '340px', objectFit: 'contain' }} />
        ) : (
          <p className="m-0 text-[10.5px] font-bold" style={{ color: INK, letterSpacing: '0.02em' }}>
            THE.K SEOUL
          </p>
        )}
        {/* The logo asset itself has ~12px of transparent padding above its
            visible wordmark at this display height, so nudge this column
            down by the same amount to line up with the logo's actual ink,
            not the image's bounding box. */}
        <div className="text-right flex-shrink-0 flex flex-col items-end" style={{ maxWidth: '320px', paddingTop: '12px' }}>
          {issuer?.address && (
            <p className="m-0 flex items-center gap-1.5 text-[10px] leading-[1.4]" style={{ color: MUTED }}>
              {issuer.address}
              <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: FAINT }} />
            </p>
          )}
          {(issuer?.contact || issuer?.email) && (
            <p className="m-0 mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: MUTED }}>
              {[issuer?.contact, issuer?.email].filter(Boolean).join('  ·  ')}
              <Mail className="w-3 h-3 flex-shrink-0" style={{ color: FAINT }} />
            </p>
          )}
          <span
            className="inline-block mt-2.5 text-[8.5px] font-bold uppercase rounded-full px-2.5 py-1"
            style={{ background: PANEL, color: ACCENT_DEEP, letterSpacing: '0.12em' }}
          >
            Partner Profile
          </span>
        </div>
      </div>

      <div className="flex-1" style={{ padding: '3mm 16mm 0', boxSizing: 'border-box' }}>
        {/* Identity block */}
        <div className="flex items-start gap-4">
          <div
            className="flex-shrink-0 rounded-xl flex items-center justify-center"
            style={{ width: '56px', height: '56px', background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)` }}
          >
            <span className="text-white font-bold" style={{ fontSize: '18px' }}>
              {initials(displayName)}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="m-0 font-bold" style={{ fontSize: '22px', letterSpacing: '-0.01em', color: INK }}>
                {displayName}
              </h1>
              <span
                className="inline-block text-[9px] font-bold uppercase rounded-full px-2.5 py-1"
                style={{ background: statusColor.bg, color: statusColor.fg, letterSpacing: '0.06em' }}
              >
                {partner.status}
              </span>
            </div>
            <p className="m-0 mt-1 text-[12px]" style={{ color: MUTED }}>
              {typeLabel}
              {partner.city && ` · ${partner.city}`}
            </p>
          </div>
        </div>

        {/* Contact + Compliance */}
        <div className="mt-8">
          <SectionLabel>Contact Information</SectionLabel>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            <IconRow
              icon={<User className="w-3.5 h-3.5" />}
              label="Contact Person"
              value={
                partner.contact_person
                  ? partner.designation
                    ? `${partner.contact_person} (${partner.designation})`
                    : partner.contact_person
                  : null
              }
            />
            <IconRow icon={<Phone className="w-3.5 h-3.5" />} label="Phone" value={partner.phone} />
            <IconRow icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={partner.email} />
          </div>
          <div className="mt-4">
            <IconRow icon={<MapPin className="w-3.5 h-3.5" />} label="Business Address" value={partner.address} />
          </div>
        </div>

        {complianceIds.length > 0 && (
          <div className="mt-8">
            <SectionLabel>Compliance &amp; Registration</SectionLabel>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${complianceIds.length}, 1fr)`, gap: '20px' }}>
              {complianceIds.map((c) => (
                <IconRow key={c.label} icon={<ShieldCheck className="w-3.5 h-3.5" />} label={c.label} value={c.value} />
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        <div className="mt-8">
          <SectionLabel>Document Verification</SectionLabel>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {DOC_ORDER.map((key) => {
              const onFile = !!partner.documents[key];
              return (
                <div
                  key={key}
                  className="flex items-center gap-1.5 rounded-lg py-2 px-2.5"
                  style={{ border: `1px solid ${onFile ? '#BBF7D0' : BORDER}`, background: onFile ? '#F0FDF4' : 'white' }}
                >
                  {onFile ? (
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: SUCCESS }} />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: FAINT }} />
                  )}
                  <span className="text-[10px] font-medium" style={{ color: onFile ? INK : FAINT }}>
                    {DOC_LABELS[key]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Agreement */}
        {(partner.agreement_terms || partner.agreement_start_date || partner.agreement_end_date) && (
          <div className="mt-8">
            <SectionLabel>Agreement</SectionLabel>
            <div className="rounded-lg p-4" style={{ background: PANEL, border: `1px solid ${BORDER}` }}>
              {(partner.agreement_start_date || partner.agreement_end_date) && (
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 flex-shrink-0" style={{ color: ACCENT }} />
                  <p className="m-0 text-[12px] font-semibold" style={{ color: INK }}>
                    Agreement Period: {formatDate(partner.agreement_start_date)} – {formatDate(partner.agreement_end_date)}
                  </p>
                </div>
              )}
              {partner.agreement_terms && (
                <div className={partner.agreement_start_date || partner.agreement_end_date ? 'mt-3 pt-3' : ''} style={(partner.agreement_start_date || partner.agreement_end_date) ? { borderTop: `1px solid ${BORDER}` } : undefined}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <ScrollText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: FAINT }} />
                    <p className="m-0 text-[9px] font-bold uppercase" style={{ letterSpacing: '0.1em', color: MUTED }}>
                      Terms &amp; Conditions
                    </p>
                  </div>
                  <p className="m-0 text-[10.5px] leading-[1.6] whitespace-pre-wrap" style={{ color: '#374151' }}>
                    {partner.agreement_terms}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Public listing */}
        <div className="mt-8">
          <SectionLabel>Public Listing</SectionLabel>
          <div className="rounded-lg p-4" style={{ background: PANEL, border: `1px solid ${BORDER}` }}>
            <div className="flex items-center gap-2">
              <Globe2 className="w-4 h-4 flex-shrink-0" style={{ color: partner.show_on_website ? SUCCESS : FAINT }} />
              <p className="m-0 text-[12px] font-semibold" style={{ color: INK }}>
                {partner.show_on_website ? 'Visible on rxk’s "Where to Buy" page' : 'Not shown on the public site'}
              </p>
            </div>
            {(partner.hours || partner.map_link) && (
              <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                {partner.hours && (
                  <span className="inline-flex items-center gap-1.5 text-[10.5px]" style={{ color: MUTED }}>
                    <Clock className="w-3 h-3" style={{ color: FAINT }} /> {partner.hours}
                  </span>
                )}
                {partner.map_link && (
                  <span className="inline-flex items-center gap-1.5 text-[10.5px]" style={{ color: '#3D5A8A' }}>
                    <MapPin className="w-3 h-3" style={{ color: FAINT }} /> {partner.map_link}
                  </span>
                )}
              </div>
            )}
            {partner.description && (
              <p className="m-0 mt-2.5 text-[10.5px]" style={{ color: MUTED }}>
                {partner.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex-shrink-0 flex items-center justify-between"
        style={{ padding: '6mm 16mm', borderTop: `1px solid ${BORDER}`, marginTop: '10mm' }}
      >
        <div className="flex items-center gap-1.5">
          <Building2 className="w-3 h-3" style={{ color: FAINT }} />
          <p className="m-0 text-[9px]" style={{ color: FAINT }}>
            Partner since {formatDate(partner.created_at)}
          </p>
        </div>
        <p className="m-0 text-[9px]" style={{ color: FAINT }}>
          Generated {formatDate(new Date().toISOString())} · The.K Seoul Partner Directory
        </p>
      </div>
    </div>
  );
}
